import { getReceiptDate } from "../../utils/receiptDate";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import "./Admin.css";

const NhapHangAdmin = () => {
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState({
    id: null,
    soPhieu: "",
    ngay: new Date(),
    khachHang: "",
    trangThai: "Đã duyệt", // Auto-approved status
    tongSoLuong: 0,
    items: [],
    bqdApproved: true, // Auto-approved by BQD
    bqdTimestamp: new Date(),
    bqdMessage: "Đã kiểm soát BQD (Tự động)",
  });

  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [storeName, setStoreName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [newProduct, setNewProduct] = useState({
    maVt: "",
    ten: "",
    soLuong: "",
    slThucNhan: "",
    dvt: "cái",
    giaMua: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success"
  });

  // Add new state for categories and products
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Fetch stores on component mount
  useEffect(() => {
    fetchStores();
    generateReceiptNumber();
  }, []);

  // Generate receipt number
  const generateReceiptNumber = () => {
    const now = new Date();
    const timestamp = now.getTime().toString().slice(-6);
    const receiptNumber = `ADM-${timestamp}`;
    setReceipt(prev => ({
      ...prev,
      soPhieu: receiptNumber,
      id: parseInt(timestamp)
    }));
  };

  // Reset page to default state
  const resetPageToDefault = () => {
    // Reset receipt state
    setReceipt({
      id: null,
      soPhieu: "",
      ngay: new Date(),
      khachHang: "",
      trangThai: "Đã duyệt",
      tongSoLuong: 0,
      items: [],
      bqdApproved: true,
      bqdTimestamp: new Date(),
      bqdMessage: "Đã kiểm soát BQD (Tự động)",
    });

    // Reset form states
    setStoreId("");
    setStoreName("");
    setShowAddProductModal(false);
    setNewProduct({
      maVt: "",
      ten: "",
      soLuong: "",
      slThucNhan: "",
      dvt: "cái",
      giaMua: "",
    });
    setFormErrors({});

    // Generate new receipt number
    generateReceiptNumber();
  };

  // Fetch stores from API
  const fetchStores = async () => {
    try {
      const response = await fetch("https://doticom.vn:3000/stores");
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const storesData = await response.json();
      console.log("Stores data structure:", storesData); // Debug log
      setStores(storesData);
    } catch (error) {
      console.error("Error fetching stores:", error);
      showNotification("Không thể tải danh sách cửa hàng", "error");
    }
  };

  // Show notification
  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "success" });
    }, 4000);
  };

  // Add function to fetch categories
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await fetch("https://doticom.vn:3000/categories");
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();
      
      const transformedCategories = data.map((cat) => ({
        id: cat.MaNLSP,
        name: cat.TenLoaiSP,
      }));
      
      setCategories(transformedCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      showNotification("Không thể tải danh mục sản phẩm", "error");
    } finally {
      setLoadingCategories(false);
    }
  };

  // Add function to fetch products by category
  const fetchProductsByCategory = async (categoryId) => {
    if (!categoryId) return;
    
    setLoadingProducts(true);
    try {
      const response = await fetch(
        `https://doticom.vn:3000/items?category=${categoryId}`
      );
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();
      
      const transformedProducts = data.map((product) => ({
        id: product.MaSP,
        name: product.Ten_SP,
        price: parseFloat(product.DonGia) || 0,
        price2: parseFloat(product.DonGia2) || 0,
        unit: product.DVT_SP || "",
      }));
      
      setProducts(prev => ({
        ...prev,
        [categoryId]: transformedProducts
      }));
    } catch (error) {
      console.error("Error fetching products:", error);
      showNotification("Không thể tải sản phẩm", "error");
    } finally {
      setLoadingProducts(false);
    }
  };

  // Reset form and states when opening add product modal
  // Handle opening add product modal
  const handleOpenAddProductModal = () => {
    setShowAddProductModal(true);
    setNewProduct({
      maVt: "",
      ten: "",
      soLuong: "",
      slThucNhan: "",
      dvt: "cái",
      giaMua: "",
    });
    setSelectedCategory("");
    setSelectedProduct("");
    setFormErrors({});
    
    // Fetch categories when opening modal
    if (categories.length === 0) {
      fetchCategories();
    }
  };

  // Handle store selection
  const handleStoreSelect = (event) => {
    const selectedStoreId = event.target.value;
    console.log("Selected Store ID:", selectedStoreId); // Debug log
    console.log("Available stores:", stores); // Debug log
    
    // Try different possible property names for store ID
    const selectedStore = stores.find((store) => 
      store.IDCH === selectedStoreId || 
      store.id === selectedStoreId || 
      store.ID === selectedStoreId ||
      store.MaCH === selectedStoreId ||
      String(store.IDCH) === selectedStoreId ||
      String(store.id) === selectedStoreId ||
      String(store.ID) === selectedStoreId ||
      String(store.MaCH) === selectedStoreId
    );
    
    console.log("Selected Store:", selectedStore);
    
    if (selectedStore) {
      setStoreId(selectedStoreId);
      // Try different possible property names for store name
      const storeName = selectedStore.TenCuaHang || 
                       selectedStore.tenCuaHang || 
                       selectedStore.name || 
                       selectedStore.Name ||
                       `Store ${selectedStoreId}`;
      setStoreName(storeName);
      setReceipt(prev => ({
        ...prev,
        khachHang: storeName,
      }));
    } else {
      console.error("Store not found for ID:", selectedStoreId);
      showNotification("Không tìm thấy thông tin cửa hàng", "error");
    }
  };

  // Handle category selection
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    setSelectedProduct("");
    
    // Reset product info
    setNewProduct(prev => ({
      ...prev,
      maVt: "",
      ten: "",
    }));
    
    // Fetch products for selected category
    if (categoryId && !products[categoryId]) {
      fetchProductsByCategory(categoryId);
    }
  };

  // Handle product selection
  const handleProductSelection = (e) => {
    const productId = e.target.value;
    setSelectedProduct(productId);
    
    if (productId && selectedCategory) {
      const selectedProductData = products[selectedCategory]?.find(p => p.id === productId);
      if (selectedProductData) {
        setNewProduct(prev => ({
          ...prev,
          maVt: selectedProductData.id,
          ten: selectedProductData.name,
          dvt: selectedProductData.unit || "cái",
          giaMua: selectedProductData.price || "",
        }));
      }
    }
  };

  // Handle product input changes
  const handleProductChange = (e) => {
    const { name, value } = e.target;
    setNewProduct(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Validate product form
  const validateProductForm = () => {
    const errors = {};

    if (!selectedCategory) {
      errors.category = "Vui lòng chọn danh mục";
    }

    if (!selectedProduct) {
      errors.product = "Vui lòng chọn sản phẩm";
    }

    if (!newProduct.soLuong || parseInt(newProduct.soLuong) <= 0) {
      errors.soLuong = "Vui lòng nhập số lượng hợp lệ";
    }

    if (!newProduct.slThucNhan || parseInt(newProduct.slThucNhan) <= 0) {
      errors.slThucNhan = "Vui lòng nhập số lượng thực nhận hợp lệ";
    }

    if (!newProduct.giaMua || parseFloat(newProduct.giaMua) <= 0) {
      errors.giaMua = "Vui lòng nhập giá mua hợp lệ";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Add product to receipt
  const handleAddProduct = () => {
    if (!validateProductForm()) {
      return;
    }

    // Check if product code already exists
    const existingProduct = receipt.items.find(item => item.maVt === newProduct.maVt);
    if (existingProduct) {
      showNotification("Sản phẩm với mã này đã tồn tại trong phiếu", "error");
      return;
    }

    // Add product to receipt
    const updatedItems = [
      ...receipt.items,
      {
        maVt: newProduct.maVt,
        ten: newProduct.ten,
        soLuong: parseInt(newProduct.soLuong),
        slThucNhan: parseInt(newProduct.slThucNhan),
        dvt: newProduct.dvt,
        giaMua: parseFloat(newProduct.giaMua),
        thanhTien: parseInt(newProduct.soLuong) * parseFloat(newProduct.giaMua),
        isCustom: true,
      },
    ];

    const totalQuantity = updatedItems.reduce((sum, item) => sum + item.soLuong, 0);

    setReceipt(prev => ({
      ...prev,
      items: updatedItems,
      tongSoLuong: totalQuantity,
    }));

    setShowAddProductModal(false);
    showNotification("Đã thêm sản phẩm thành công", "success");
  };

  // Remove product from receipt
  const handleRemoveProduct = (index) => {
    const updatedItems = receipt.items.filter((_, i) => i !== index);
    const totalQuantity = updatedItems.reduce((sum, item) => sum + item.soLuong, 0);

    setReceipt(prev => ({
      ...prev,
      items: updatedItems,
      tongSoLuong: totalQuantity,
    }));

    showNotification("Đã xóa sản phẩm", "success");
  };

  // Update product in receipt
  const handleUpdateProduct = (index, field, value) => {
    const updatedItems = [...receipt.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: field === 'soLuong' || field === 'slThucNhan' ? parseInt(value) || 0 : value,
    };

    // Recalculate thanhTien if soLuong or giaMua changed
    if (field === 'soLuong' || field === 'giaMua') {
      updatedItems[index].thanhTien = (updatedItems[index].soLuong || 0) * (updatedItems[index].giaMua || 0);
    }

    const totalQuantity = updatedItems.reduce((sum, item) => sum + item.soLuong, 0);

    setReceipt(prev => ({
      ...prev,
      items: updatedItems,
      tongSoLuong: totalQuantity,
    }));
  };

  // Submit receipt to API
  const handleSubmitReceipt = async () => {
    if (!storeId) {
      showNotification("Vui lòng chọn cửa hàng", "error");
      return;
    }

    if (receipt.items.length === 0) {
      showNotification("Vui lòng thêm ít nhất một sản phẩm", "error");
      return;
    }

    setLoading(true);

    try {
      const requestBody = {
        phieu: {
          MaKH: storeId.toString(),
          NgayNhap: getReceiptDate(),
          TenUser: localStorage.getItem("TenUser") || "Admin",
          GhiChu: `Phiếu nhập admin ${receipt.soPhieu}`,
          MaCH: storeId.toString(),
        },
        chitiet: receipt.items.map((item) => ({
          MaSP: item.maVt,
          TenSP: item.ten,
          SoLuong: 0, // Set to 0 as per existing pattern
          SoLuongTN: item.slThucNhan,
          SoLuongNhapDau: item.soLuong,
          GiaMua: item.giaMua,
          GiamPT: 0,
          ThanhTien: item.thanhTien,
        })),
      };

      console.log("Sending receipt data:", requestBody);

      const response = await fetch("https://doticom.vn:3000/nhap-hang", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create receipt");
      }

      const data = await response.json();
      console.log("Receipt creation response:", data);
      
      // Get receipt ID from the API response (maPhieuNhap)
      const receiptId = data.maPhieuNhap;
      console.log("Extracted receipt ID (maPhieuNhap):", receiptId);

      if (!receiptId) {
        throw new Error("Không nhận được mã phiếu nhập từ server");
      }

      // Automatically approve the receipt
      console.log("Starting auto-approval process...");
      const autoApprovalSuccess = await autoApproveReceipt(receiptId);

      if (autoApprovalSuccess) {
        showNotification("Phiếu nhập đã được tạo và duyệt thành công!", "success");
        
        // Reset page to default state after successful creation and approval
        setTimeout(() => {
          resetPageToDefault();
        }, 1500);
      } else {
        showNotification("Phiếu nhập đã được tạo nhưng việc duyệt tự động thất bại. Vui lòng kiểm tra lại.", "warning");
      }

    } catch (error) {
      console.error("Error creating receipt:", error);
      showNotification(`Lỗi khi tạo phiếu nhập: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  // Auto-approve receipt
  const autoApproveReceipt = async (receiptId) => {
    try {
      console.log("Starting auto-approval for receipt ID:", receiptId);
      
      const requestBody = {
        AdminCreated: true, 
        maPhieuNhap: receiptId,
        chiTietSanPham: receipt.items.map((item) => ({
          maSP: item.maVt,
          slThucNhan: item.slThucNhan,
        })),
      };

      console.log("Auto-approval request body:", requestBody);

      const response = await fetch(
        "https://doticom.vn:3000/api/xac-nhan-kiem-soat",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      console.log("Auto-approval response status:", response.status);

      if (!response.ok) {
        const errorData = await response.text();
        console.error("Auto-approval failed with error:", errorData);
        return false;
      }

      const approvalData = await response.json();
      console.log("Auto-approval response data:", approvalData);
      return true;

    } catch (error) {
      console.error("Auto-approval error:", error);
      return false;
    }
  };

  // Format date for display
  const formatDate = (date) => {
    return format(date, "dd/MM/yyyy", { locale: vi });
  };

  return (
    <div className="admin-layout">
      <div className="admin-main" style={{ marginLeft: 0 }}>
        {/* Notification */}
        {notification.show && (
          <div className={`alert alert-${notification.type === 'error' ? 'danger' : 'success'}`} 
               style={{
                 position: 'fixed',
                 top: '20px',
                 right: '20px',
                 zIndex: 9999,
                 minWidth: '300px',
                 padding: '12px 20px',
                 borderRadius: '8px',
                 backgroundColor: notification.type === 'error' ? '#f8d7da' : '#d4edda',
                 color: notification.type === 'error' ? '#721c24' : '#155724',
                 border: `1px solid ${notification.type === 'error' ? '#f5c6cb' : '#c3e6cb'}`,
                 boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
               }}>
            {notification.message}
          </div>
        )}

        {/* Header */}
        <div className="admin-header">
          <div className="header-left">
            <h1>Tạo Phiếu Nhập Admin</h1>
            <p>Tạo phiếu nhập với quyền admin - tự động duyệt</p>
          </div>
          <div className="header-right">
            <button 
              className="btn btn-secondary me-2"
              onClick={() => navigate("/admin")}
            >
              ← Quay lại Admin
            </button>
            <button 
              className="btn btn-primary"
              onClick={handleSubmitReceipt}
              disabled={loading || !storeId || receipt.items.length === 0}
            >
              {loading ? "Đang xử lý..." : "Tạo & Duyệt Phiếu"}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="content-section">
          {/* Store Selection Card */}
          <div className="card mb-3">
            <div className="card-header">
              <h3 className="mb-0">Thông tin cửa hàng</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="storeSelect">Chọn cửa hàng *</label>
                    <select
                      id="storeSelect"
                      className="form-select"
                      value={storeId}
                      onChange={handleStoreSelect}
                      required
                    >
                      <option value="">-- Chọn cửa hàng --</option>
                      {stores.map((store) => (
                        <option key={store.IDCH || store.id || store.ID || store.MaCH} value={store.IDCH || store.id || store.ID || store.MaCH}>
                          {(store.TenCuaHang || store.tenCuaHang || store.name || store.Name || `Store ${store.IDCH || store.id || store.ID || store.MaCH}`)} ({store.IDCH || store.id || store.ID || store.MaCH})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Số phiếu</label>
                    <input
                      type="text"
                      className="form-control"
                      value={receipt.soPhieu}
                      readOnly
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Ngày tạo</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formatDate(receipt.ngay)}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Products Card */}
          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Danh sách sản phẩm</h3>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleOpenAddProductModal}
                  disabled={!storeId}
                >
                  <i className="fas fa-plus me-1"></i>
                  Thêm sản phẩm
                </button>
              </div>
            </div>
            <div className="card-body">
              {receipt.items.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
                  <p className="text-muted">
                    {!storeId 
                      ? "Vui lòng chọn cửa hàng để thêm sản phẩm"
                      : "Chưa có sản phẩm nào. Nhấn 'Thêm sản phẩm' để bắt đầu."
                    }
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Mã SP</th>
                        <th>Tên sản phẩm</th>
                        <th>ĐVT</th>
                        <th>Số lượng</th>
                        <th>SL thực nhận</th>
                        <th>Giá mua</th>
                        <th>Thành tiền</th>
                        <th>Thao tác</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receipt.items.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.maVt}
                              onChange={(e) => handleUpdateProduct(index, 'maVt', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.ten}
                              onChange={(e) => handleUpdateProduct(index, 'ten', e.target.value)}
                            />
                          </td>
                          <td>
                            <select
                              className="form-select form-select-sm"
                              value={item.dvt}
                              onChange={(e) => handleUpdateProduct(index, 'dvt', e.target.value)}
                            >
                              <option value="cái">Cái</option>
                              <option value="hộp">Hộp</option>
                              <option value="kg">Kg</option>
                              <option value="gói">Gói</option>
                              <option value="miếng">Miếng</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={item.soLuong}
                              onChange={(e) => handleUpdateProduct(index, 'soLuong', e.target.value)}
                              min="0"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={item.slThucNhan}
                              onChange={(e) => handleUpdateProduct(index, 'slThucNhan', e.target.value)}
                              min="0"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={item.giaMua}
                              onChange={(e) => handleUpdateProduct(index, 'giaMua', e.target.value)}
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td>
                            <span className="text-primary fw-bold">
                              {(item.thanhTien || 0).toLocaleString('vi-VN')}đ
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleRemoveProduct(index)}
                              title="Xóa sản phẩm"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-info">
                        <td colSpan="3"><strong>Tổng cộng:</strong></td>
                        <td><strong>{receipt.tongSoLuong}</strong></td>
                        <td><strong>{receipt.items.reduce((sum, item) => sum + (item.slThucNhan || 0), 0)}</strong></td>
                        <td></td>
                        <td>
                          <strong className="text-primary">
                            {receipt.items.reduce((sum, item) => sum + (item.thanhTien || 0), 0).toLocaleString('vi-VN')}đ
                          </strong>
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add Product Modal */}
        {showAddProductModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Thêm sản phẩm mới</h3>
                <button
                  className="modal-close"
                  onClick={() => setShowAddProductModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {/* Category Selection */}
                <div className="form-group">
                  <label htmlFor="categorySelect">Danh mục sản phẩm *</label>
                  <select
                    id="categorySelect"
                    className={`form-select ${formErrors.category ? 'is-invalid' : ''}`}
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    disabled={loadingCategories}
                  >
                    <option value="">-- Chọn danh mục --</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.category && (
                    <div className="invalid-feedback">{formErrors.category}</div>
                  )}
                </div>

                {/* Product Selection */}
                <div className="form-group">
                  <label htmlFor="productSelect">Sản phẩm *</label>
                  <select
                    id="productSelect"
                    className={`form-select ${formErrors.product ? 'is-invalid' : ''}`}
                    value={selectedProduct}
                    onChange={handleProductSelection}
                    disabled={!selectedCategory || loadingProducts}
                  >
                    <option value="">-- Chọn sản phẩm --</option>
                    {selectedCategory && products[selectedCategory]?.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} {product.unit && `(${product.unit})`}
                      </option>
                    ))}
                  </select>
                  {formErrors.product && (
                    <div className="invalid-feedback">{formErrors.product}</div>
                  )}
                </div>

                {/* Product Info Display */}
                {selectedProduct && (
                  <div className="form-group">
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '4px',
                      border: '1px solid #e9ecef'
                    }}>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        <strong>Mã sản phẩm:</strong> {newProduct.maVt}
                      </p>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        <strong>Tên sản phẩm:</strong> {newProduct.ten}
                      </p>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        <strong>Đơn vị tính:</strong> {newProduct.dvt}
                      </p>
                    </div>
                  </div>
                )}

                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="productQuantity">Số lượng *</label>
                      <input
                        type="number"
                        id="productQuantity"
                        name="soLuong"
                        className={`form-control ${formErrors.soLuong ? 'is-invalid' : ''}`}
                        value={newProduct.soLuong}
                        onChange={handleProductChange}
                        placeholder="0"
                        min="1"
                      />
                      {formErrors.soLuong && (
                        <div className="invalid-feedback">{formErrors.soLuong}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="actualQuantity">SL thực nhận *</label>
                      <input
                        type="number"
                        id="actualQuantity"
                        name="slThucNhan"
                        className={`form-control ${formErrors.slThucNhan ? 'is-invalid' : ''}`}
                        value={newProduct.slThucNhan}
                        onChange={handleProductChange}
                        placeholder="0"
                        min="0"
                      />
                      {formErrors.slThucNhan && (
                        <div className="invalid-feedback">{formErrors.slThucNhan}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="productPrice">Giá mua *</label>
                      <input
                        type="number"
                        id="productPrice"
                        name="giaMua"
                        className={`form-control ${formErrors.giaMua ? 'is-invalid' : ''}`}
                        value={newProduct.giaMua}
                        onChange={handleProductChange}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                      {formErrors.giaMua && (
                        <div className="invalid-feedback">{formErrors.giaMua}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowAddProductModal(false)}
                >
                  Hủy bỏ
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleAddProduct}
                  disabled={!selectedProduct}
                >
                  Thêm sản phẩm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NhapHangAdmin;
